import { useRef, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
// MUI
import { Grid, Stack } from "@mui/material";
// Icons
import PublicIcon from "@mui/icons-material/Public";
// Images
import empty from "@/assets/images/commons/empty.png";
// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useLocations } from "@/Common/hooks/Location/useLocations";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
//API
import {
	addUserAuthorization,
	addUserAuthorizationLocation,
	deleteUserAuthorization,
	deleteUserAuthorizationLocation,
} from "../../api/UserAccessSettings/mutations";
import { getUserAccessSettings } from "../../api/UserAccessSettings/queries";
// Constants
import actions from "@/Constants/actionsList";

// Components
import AddLocation from "./AddLocation";
import UserAccessView from "./UserAccessView";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import ScrollSideBar from "@/Layouts/components/SideBars/ScrollSideBar/ScrollSideBar";
import ContentScrollable from "@/Layouts/components/ContentScrollable";
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
import { cloneDeep } from "lodash";

function isGlobalLocation(locationId = "") {
	try {
		let compare = locationId;
		return compare.slice(locationId.length - 5) === "00000";
	} catch (error) {
		console.log(error);
	}

	return false;
}

function getLocationName(locations = [], locationId) {
	let name = "Undefined";

	if (isGlobalLocation(locationId)) {
		return "Global";
	}

	let location = locations.find((element) => element.locationId === locationId);

	if (location) {
		name = location.name;
	}

	return name;
}

function getLocationLogo(locations = [], locationId) {
	let logo = "";

	if (isGlobalLocation(locationId)) {
		return {
			type: "icon",
			icon: <PublicIcon sx={{ mt: "0px", fontSize: "30px" }} />,
		};
	}

	let location = locations.find((element) => element.locationId === locationId);

	if (location) {
		logo = {
			type: "image",
			icon: location.logo,
		};
	}

	return logo;
}

function getIndividualData(data = [], index) {
	let result = data.find((element) => element._id === index);
	return result;
}

function getAvailableLocations(locations = [], usedLocations = []) {
	let result = locations.filter((element) => !usedLocations.find((l) => element.locationId === l.locationId));
	return result;
}

export default function UserAccessSettingsModel(props) {
	const subscriptionId = useRef(null);
	const { enqueueSnackbar } = useSnackbar();

	const [settings, setSettings] = useState([]);
	const [selected, setSelected] = useState(null);
	const [addAmount, setAddAmount] = useState(1);
	const [deleting, setDeleting] = useState({
		action: false,
		group: false,
	});

	const [addingOnLocation, setAddingOnLocation] = useState(null);
	const [deletingLocation, setDeletingLocation] = useState(null);

	const tenant = "0000100000";
	const { getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const editable = useAuth(actions.EDIT_USER_ACCESS_SETTINGS);

	const { locations, globalLocations, isLoading: isLoadingLocations } = useLocations(tenant);

	// Subscriptions
	const userAccessSettingsSubscription = getSubscription(["userAccessSettings", props.selectedUser?._id]);
	userAccessSettingsSubscription?.setInvalidate(["userAccessSettings", props.selectedUser?._id]);

	userAccessSettingsSubscription?.subscriptions?.onAddUserAuthorization?.setCallback((result) => {
		const user = result.data.onAddUserAuthorization;
		if (!user?.auth?.length) {
			return;
		}
		const userId = userAccessSettingsSubscription.getLastKeyIdentifier();
		queryClient.setQueryData(["userAccessSettings", userId], (data) => {
			const updatedData = cloneDeep(data);

			user.auth.forEach((auth) => {
				const objectKey = auth.groups?.length ? "groups" : "actions";

				data.auth.forEach((item, index) => {
					if (item.locationId !== auth.locationId) {
						return;
					}
					if (auth[objectKey]?.length && item?.[objectKey]) {
						updatedData.auth[index][objectKey] = [...item[objectKey], ...auth[objectKey]];
						return;
					}
					if (auth[objectKey]) {
						updatedData.auth[index][objectKey] = [...auth[objectKey]];
					}
				});
			});
			return updatedData;
		});
	});

	userAccessSettingsSubscription?.subscriptions?.onDeleteUserAuthorization?.setCallback((result) => {
		let user = result.data.onDeleteUserAuthorization;
		let userId = userAccessSettingsSubscription.getLastKeyIdentifier();

		if (user?.auth?.length) {
			queryClient.setQueryData(["userAccessSettings", userId], (data) => {
				user.auth.forEach((auth) => {
					let modify = auth.groups?.length ? "groups" : "actions";
					let mainKey = auth.groups?.length ? "_id" : "actionKey";

					let keys = [];
					auth[modify]?.forEach((item) => {
						keys.push(item[mainKey]);
					});

					data.auth.forEach((element, key) => {
						if (element.locationId === auth.locationId) {
							if (auth[modify]?.length) {
								data.auth[key][modify] = element[modify].filter((element) => !keys.includes(element[mainKey]));
							}
						}
					});
				});

				return data;
			});
		}

		if (subscriptionId.current === userId) {
			setDeleting({
				action: false,
				group: false,
			});
		}
	});

	userAccessSettingsSubscription?.subscriptions?.onAddUserAuthorizationLocation?.setCallback((result) => {
		let user = result.data.onAddUserAuthorizationLocation;
		if (user?.auth?.length) {
			let userId = userAccessSettingsSubscription.getLastKeyIdentifier();
			queryClient.setQueryData(["userAccessSettings", userId], (data) => {
				let authorization = {
					_id: user.auth[0]._id,
					locationId: user.auth[0].locationId,
					name: getLocationName(locations, user.auth[0].locationId),
					logo: getLocationLogo(locations, user.auth[0].locationId),
				};
				data.auth.push(authorization);
				return data;
			});
		}
	});

	userAccessSettingsSubscription?.subscriptions?.onDeleteUserAuthorizationLocation?.setCallback((result) => {
		let user = result.data.onDeleteUserAuthorizationLocation;
		if (user?.auth?.length) {
			let userId = userAccessSettingsSubscription.getLastKeyIdentifier();
			queryClient.setQueryData(["userAccessSettings", userId], (data) => {
				user.auth.forEach((auth) => {
					data.auth = data.auth.filter((element) => element.locationId !== auth.locationId);
				});

				return data;
			});

			if (subscriptionId.current === userId) {
				setDeletingLocation(null);
			}
		}
	});

	// Queries
	const { data, isLoading } = useQuery({
		queryKey: ["userAccessSettings", props.selectedUser?._id],
		queryFn: async () => {
			let user = await getUserAccessSettings(props.selectedUser._id, props.location.locationId);
			let settingsMenu = [];

			user.auth?.forEach((item) => {
				settingsMenu.push({
					_id: item._id,
					locationId: item.locationId,
					name: getLocationName(locations, item.locationId),
					logo: getLocationLogo(locations, item.locationId),
				});
			});

			settingsMenu = settingsMenu.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : itemB.name > itemA.name ? -1 : 0));
			setSettings(settingsMenu);
			setDeletingLocation(null);
			return user;
		},
		enabled: !!locations?.length && !!props.location?.locationId && !!props.selectedUser?._id,
		staleTime: Infinity,
	});

	const { mutate: addLocation, isPending: addingLocation } = useMutation({
		mutationFn: (selectedLocation) => addUserAuthorizationLocation(props.location.locationId, props.selectedUser._id, selectedLocation),
		onSuccess: (result) => {
			if (result?.auth) {
				let currentSettings = [...settings];

				let authorization = {
					_id: result.auth[0]._id,
					locationId: result.auth[0].locationId,
					name: getLocationName(locations, result.auth[0].locationId),
					logo: getLocationLogo(locations, result.auth[0].locationId),
				};

				currentSettings.push(authorization);
				currentSettings = currentSettings.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : itemB.name > itemA.name ? -1 : 0));
				setSettings(currentSettings);
			}
		},
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred trying to add a location."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: (key) => <SnackbarsActions key={key} index={key} />,
			});
		},
	});

	const { mutate: deleteLocation } = useMutation({
		mutationFn: (locationId) => deleteUserAuthorizationLocation(props.location.locationId, props.selectedUser._id, locationId),
		onSuccess: (result) => {
			if (result?.auth?.length) {
				let currentSettings = [...settings];
				let remove = [];
				result.auth.forEach((auth) => {
					remove.push(auth.locationId);
				});

				currentSettings = currentSettings.filter((setting) => !remove.includes(setting.locationId));
				currentSettings = currentSettings.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : itemB.name > itemA.name ? -1 : 0));
				setSettings(currentSettings);
			}
		},
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred trying to delete a location."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: (key) => <SnackbarsActions key={key} index={key} />,
			});
		},
	});

	const { mutate: mutateAddActions, isPending: addingActions } = useMutation({
		mutationFn: (authorization) => addUserAuthorization(props.location.locationId, props.selectedUser._id, authorization),
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred trying to add actions."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: (key) => <SnackbarsActions key={key} index={key} />,
			});
		},
	});

	const { mutate: mutateAddGroups, isPending: addingGroups } = useMutation({
		mutationFn: (authorization) => addUserAuthorization(props.location.locationId, props.selectedUser._id, authorization),
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred trying to add groups."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: (key) => <SnackbarsActions key={key} index={key} />,
			});
		},
	});

	const { mutate: mutateDeleteActions } = useMutation({
		mutationFn: (authorization) => deleteUserAuthorization(props.location.locationId, props.selectedUser._id, authorization),
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred trying to delete an action."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: (key) => <SnackbarsActions key={key} index={key} />,
			});
		},
	});

	const { mutate: mutateDeleteGroups } = useMutation({
		mutationFn: (authorization) => deleteUserAuthorization(props.location.locationId, props.selectedUser._id, authorization),
		onError: () => {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred trying to delete a group."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: (key) => <SnackbarsActions key={key} index={key} />,
			});
		},
	});

	const handleClick = (item) => {
		if (item?._id !== selected?._id) {
			setSelected(item);
		}
	};

	const handleAddLocation = (selectedLocation) => {
		if (selectedLocation) {
			addLocation(selectedLocation.locationId);
		}
	};

	const handleDeleteLocation = (selectedLocation) => {
		if (selectedLocation) {
			setDeletingLocation(selectedLocation._id);
			deleteLocation(selectedLocation.locationId);
		}
	};

	const handleAddGroups = (groups, locationId) => {
		let authorization = [];
		if (groups?.length && locationId) {
			let add = {
				locationId: locationId,
				actions: [],
				groups: [],
			};

			let groupsKeys = [];
			groups.forEach((element) => {
				groupsKeys.push(element._id);
			});

			add.groups = groupsKeys;
			authorization.push(add);
			mutateAddGroups(authorization);

			setAddAmount(groupsKeys.length);
			setAddingOnLocation(locationId);
		}
	};

	const handleDeleteGroups = (groups, locationId) => {
		let authorization = [];
		if (groups?.length && locationId) {
			let remove = {
				locationId: locationId,
				actions: [],
				groups: [],
			};

			let groupsKeys = [];
			groups.forEach((element) => {
				groupsKeys.push(element._id);
			});

			remove.groups = groupsKeys;
			authorization.push(remove);
			mutateDeleteGroups(authorization);

			setDeleting({ ...deleting, group: true });
		}
	};

	const handleAddActions = (actions = [], locationId) => {
		let authorization = [];
		if (actions?.length && locationId) {
			let add = {
				locationId: locationId,
				actions: [],
				groups: [],
			};

			let actionsKeys = [];
			actions.forEach((element) => {
				actionsKeys.push(element.actionKey);
			});

			add.actions = actionsKeys;

			authorization.push(add);
			mutateAddActions(authorization);

			setAddingOnLocation(locationId);
			setAddAmount(actionsKeys.length);
		}
	};

	const handleDeleteActions = (actions, locationId) => {
		let authorization = [];
		if (actions?.length && locationId) {
			let remove = {
				locationId: locationId,
				actions: [],
				groups: [],
			};

			let actionsKeys = [];
			actions.forEach((element) => {
				actionsKeys.push(element.actionKey);
			});

			remove.actions = actionsKeys;
			authorization.push(remove);
			mutateDeleteActions(authorization);

			setDeleting({ ...deleting, action: true });
		}
	};

	useEffect(() => {
		if (props.selectedUser) {
			subscriptionId.current = props.selectedUser._id;
			let currentData = queryClient.getQueryData(["userAccessSettings", props.selectedUser._id]);
			if (currentData) {
				let settingsMenu = [];

				currentData.auth?.forEach((item) => {
					settingsMenu.push({
						_id: item._id,
						locationId: item.locationId,
						name: getLocationName(locations, item.locationId),
						logo: getLocationLogo(locations, item.locationId),
					});
				});
				settingsMenu = settingsMenu.sort((itemA, itemB) => (itemA.name > itemB.name ? 1 : itemB.name > itemA.name ? -1 : 0));
				setSettings(settingsMenu);
			}
		}

		if (props.isLoadingSideBar) return;

		return () => {
			subscriptionId.current = null;
			setAddAmount(1);
			setSettings([]);
			setSelected(null);
			setDeleting({
				action: false,
				group: false,
			});
			setAddingOnLocation(null);
			setDeletingLocation(null);
		};
	}, [props, locations, queryClient]);

	return (
		<>
			{!Boolean(isLoading || isLoadingLocations) && !Boolean(props.selectedUser) && (
				<EmptyBody
					image={empty}
					title={"Select an user to view their access settings"}
					bodyHeight={"500px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			)}

			{Boolean(props.selectedUser) && (
				<Grid container spacing={3}>
					<Grid item xs={2} sx={{ minWidth: "260px" }}>
						<ScrollSideBar
							title={"Locations"}
							position={"sticky"}
							top={"120px"}
							menus={settings}
							selected={selected?._id}
							showBy={"name"}
							scrollBy={"locationId"}
							loading={isLoading || isLoadingLocations}
							adding={addingLocation}
							deleting={deletingLocation}
							defaultSelection
							addProperties={{
								title: "Add Location",
								isClearComponent: true,
								component: (
									<Stack mt={1}>
										<AddLocation
											data={getAvailableLocations(globalLocations, data?.auth) || []}
											loading={isLoading || isLoadingLocations}
											disabled={!editable}
											onSelect={handleAddLocation}
										/>
									</Stack>
								),
							}}
							onActionClick={handleClick}
						/>
					</Grid>

					<Grid item xs={true} pt={"0px !important"} mt={"24px"}>
						<ContentScrollable scrollTo={selected?.locationId}>
							{isLoading || isLoadingLocations ? (
								<Grid container spacing={2}>
									<UserAccessView index={"loading_user_access"} logo={{}} loading={true} />
								</Grid>
							) : (
								<Grid container>
									{settings.map((element, key) => {
										return (
											<UserAccessView
												key={key}
												index={element.locationId}
												locationId={element.locationId}
												title={element.name}
												logo={element.logo}
												divider={Boolean(settings.length > 1 && settings?.length !== key + 1)}
												adding={{
													actions: addingActions && addingOnLocation === element.locationId,
													groups: addingGroups && addingOnLocation === element.locationId,
												}}
												deleting={deleting}
												loading={deletingLocation === element._id}
												addAmount={addAmount}
												data={getIndividualData(data?.auth, element._id)}
												isEditable={editable}
												onAddActions={handleAddActions}
												onDeleteActions={handleDeleteActions}
												onAddGroups={handleAddGroups}
												onDeleteGroups={handleDeleteGroups}
												onDeleteLocation={() => handleDeleteLocation(element)}
											/>
										);
									})}
								</Grid>
							)}

							{addingLocation && (
								<Grid container spacing={2} mt={1}>
									<UserAccessView index={"loading_adding"} logo={{}} loading={true} />
								</Grid>
							)}
						</ContentScrollable>
					</Grid>
				</Grid>
			)}
		</>
	);
}
